"use client";

import React from 'react';
import { getCanonicalUrl } from './utils/seo';

interface PrintItem {
    name: string;
    value: string;
    details?: string;
}

interface ProfessionalPrintLayoutProps {
    title: string;
    subtitle?: string;
    items: PrintItem[];
    total?: { label: string, value: string };
    footerText?: string;
}

export const ProfessionalPrintLayout: React.FC<ProfessionalPrintLayoutProps> = ({ 
    title, 
    subtitle, 
    items, 
    total, 
    footerText = "Designed by Sweety's Bakery - Professional Management Suite" 
}) => {
    const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <div className="hidden print:block p-8 bg-white text-black min-h-screen">
            {/* Header / Branding */}
            <header className="flex justify-between items-start border-b-2 border-black pb-6 mb-8">
                <div>
                    <h1 className="text-4xl font-serif font-bold text-gray-900 leading-none mb-1">Sweety's Bakery</h1>
                    <p className="text-sm font-bold tracking-widest uppercase text-gray-500">Premium Baking Solutions</p>
                    <p className="text-xs text-gray-400 mt-1">www.sweetysbakery.com</p>
                </div>
                <div className="text-right">
                    <div className="text-xl font-bold uppercase tracking-tighter mb-1">{title}</div>
                    <div className="text-xs text-gray-500">{today}</div>
                    {subtitle && <div className="text-sm font-medium mt-2 italic text-gray-700">{subtitle}</div>}
                </div>
            </header>

            {/* Main Content Table */}
            <main className="mb-12">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="py-3 font-bold uppercase text-xs tracking-wider text-gray-600">Component / Ingredient</th>
                            <th className="py-3 font-bold uppercase text-xs tracking-wider text-gray-600 text-right">Details / Calculation</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {items.map((item, idx) => (
                            <tr key={idx}>
                                <td className="py-4">
                                    <div className="font-bold text-gray-800">{item.name}</div>
                                </td>
                                <td className="py-4 text-right">
                                    <div className="font-medium text-gray-900">{item.value}</div>
                                    {item.details && <div className="text-xs text-gray-500 mt-0.5">{item.details}</div>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </main>

            {/* Total Section */}
            {total && (
                <div className="flex justify-end mb-12">
                    <div className="w-64 bg-gray-50 p-6 rounded-lg border-t-4 border-black">
                        <div className="text-xs font-bold uppercase text-gray-500 mb-2">{total.label}</div>
                        <div className="text-4xl font-bold text-gray-900 leading-none">{total.value}</div>
                    </div>
                </div>
            )}

            {/* Footer / Call to Action */}
            <footer className="mt-auto pt-12 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-400 italic mb-4">{footerText}</p>
                <div className="flex flex-col items-center">
                    <div className="text-[10px] font-bold text-gray-300 uppercase tracking-widest mb-2">Scan to re-calculate</div>
                    <div className="w-16 h-16 bg-gray-100 flex items-center justify-center border border-gray-200 rounded">
                        <span className="text-[8px] text-gray-400 font-bold">QR CODE</span>
                    </div>
                    <p className="text-[9px] text-gray-400 mt-2 font-mono">{getCanonicalUrl('/')}</p>
                </div>
            </footer>
        </div>
    );
};
